/**
 * Enable Netly Pay — what a shop with no workspace sees.
 *
 * Reached when `GET /netlise/v1/link` answers 409 `not_linked`, which is a
 * state and not an error (D54). The wizard is the only place in the dashboard
 * that creates or attaches a workspace, and only a shop owner may use it
 * (D53); a non-owner sees the benefits and is told who can turn it on.
 *
 * Spec: DESIGN-BRIEF.md §A and `vEnable()` in prototype.html.
 */
import React, {useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import NetlyPayRepository, {
  type CreateWorkspaceInput,
  type LinkWorkspaceInput,
} from '../../../internals/repository/NetlyPayRepository';
import {GhostButton, Modal, PrimaryButton} from '../components/Overlays';

const BENEFITS = [
  {
    n: '1',
    title: 'Bill a whole batch at once',
    text: 'Monthly fees go out on schedule with reminders and late fees.',
  },
  {
    n: '2',
    title: 'Approve payment proofs in one queue',
    text: 'Bank slips and LankaQR receipts arrive with the request they pay.',
  },
  {
    n: '3',
    title: 'See who paid and who trained',
    text: 'Coaches mark attendance in the app. You see it next to each balance.',
  },
];

const BUSINESS_TYPES: {id: CreateWorkspaceInput['template']; label: string; desc: string}[] = [
  {id: 'academy', label: 'Academy or club', desc: 'Teams and age groups that train every week.'},
  {id: 'tuition', label: 'Tuition classes', desc: 'Subjects and classes with monthly fees.'},
  {id: 'tutor', label: 'Private tutor', desc: 'One-to-one or small groups, billed per session.'},
  {id: 'gym', label: 'Gym', desc: 'Memberships and packages with check-ins.'},
];

const STEP_TITLES = ['Business type', 'Account', 'Payment methods'];

const inputCls =
  'w-full h-10 px-3 rounded-lg border border-borderc2 bg-surface text-[13.5px] text-text1 outline-none focus:border-brand transition-colors';

const Radio = ({on}: {on: boolean}) => (
  <span
    className={`w-[18px] h-[18px] rounded-full border-[1.5px] flex items-center justify-center flex-shrink-0 transition-colors ${
      on ? 'border-brand' : 'border-borderc2'
    }`}
  >
    {on && <span className="w-[9px] h-[9px] rounded-full bg-brand" />}
  </span>
);

const Switch = ({on, onClick}: {on: boolean; onClick: () => void}) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    onClick={onClick}
    className={`w-[42px] h-[24px] rounded-full p-[3px] flex-shrink-0 transition-colors ${
      on ? 'bg-brand' : 'bg-surface3'
    }`}
  >
    <span
      className={`block w-[18px] h-[18px] rounded-full bg-white transition-transform ${
        on ? 'translate-x-[18px]' : ''
      }`}
      style={{boxShadow: '0 1px 2px rgba(15,15,26,.2)'}}
    />
  </button>
);

interface Props {
  shopId: string;
  shop?: any;
  /** False for a dashboard user who is not the shop owner. */
  canEnable?: boolean;
  onEnabled?: () => void;
}

const EnableNetlyPayView = ({shopId, shop, canEnable = true, onEnabled}: Props) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [template, setTemplate] = useState<CreateWorkspaceInput['template'] | null>(null);
  const [mode, setMode] = useState<'create' | 'link'>('create');
  const [workspaceId, setWorkspaceId] = useState('');
  const [bankOn, setBankOn] = useState(true);
  const [qrOn, setQrOn] = useState(false);
  const [cashOn, setCashOn] = useState(true);
  const [bank, setBank] = useState({
    bank: '',
    branch: '',
    name: shop?.shopName ?? '',
    number: '',
  });

  const valid = useMemo(() => {
    if (step === 1) return !!template;
    if (step === 2) return mode === 'create' || !!workspaceId.trim();
    if (step === 3) return bankOn || qrOn || cashOn;
    return true;
  }, [step, template, mode, workspaceId, bankOn, qrOn, cashOn]);

  const reset = () => {
    setOpen(false);
    setStep(1);
    setTouched(false);
    setError(null);
  };

  const submit = async () => {
    const input: CreateWorkspaceInput | LinkWorkspaceInput =
      mode === 'link'
        ? {mode: 'link', workspaceId: workspaceId.trim()}
        : {
            mode: 'create',
            name: shop?.shopName ?? 'My business',
            template: template as CreateWorkspaceInput['template'],
            currency: shop?.currency ?? 'LKR',
            methods: {
              bank: bankOn ? bank : null,
              lankaqr: qrOn,
              cash: cashOn,
            },
          };

    setSaving(true);
    setError(null);
    try {
      await NetlyPayRepository.createLink(shopId, input);
      setStep(4);
    } catch (e: any) {
      // The API's own words: owner_mismatch, already_linked, workspace_taken,
      // no_payment_method. Showing the raw code would be useless to an owner.
      const messages: Record<string, string> = {
        owner_mismatch:
          'That Netly Pay account belongs to a different email. Netlise support can link it for you.',
        already_linked: 'This shop is already connected to a Netly Pay account.',
        workspace_taken: 'That Netly Pay account is already connected to another shop.',
        no_payment_method: 'Turn on at least one way to take payment.',
        forbidden: 'Only the shop owner can turn Netly Pay on.',
      };
      setError(messages[e?.code] ?? e?.message ?? 'Could not turn Netly Pay on.');
    } finally {
      setSaving(false);
    }
  };

  const next = () => {
    if (!valid) {
      setTouched(true);
      return;
    }
    setTouched(false);
    if (step === 3) {
      submit();
      return;
    }
    setStep(step + 1);
  };

  const finish = (to?: string) => {
    reset();
    onEnabled?.();
    if (to) navigate(to);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-6 lg:grid-cols-[1.2fr_1fr] items-stretch">
        <div className="bg-surface border border-borderc rounded-card p-7">
          <span className="text-[11px] font-semibold tracking-[.06em] text-brand">NETLY PAY</span>
          <h2 className="text-[26px] font-semibold text-text1 tracking-[-0.01em] mt-2 leading-tight">
            Collect fees without chasing people
          </h2>
          <p className="text-[13.5px] text-text2 mt-2 max-w-[46ch]">
            Members, monthly fees, payment proofs and attendance, in the dashboard you already use
            for the counter.
          </p>

          <div className="flex flex-col gap-4 mt-6">
            {BENEFITS.map((b) => (
              <div key={b.n} className="flex gap-3.5">
                <span className="w-7 h-7 rounded-lg bg-brand-soft text-brand text-[12px] font-semibold flex items-center justify-center flex-shrink-0">
                  {b.n}
                </span>
                <div>
                  <p className="text-[13.5px] font-semibold text-text1">{b.title}</p>
                  <p className="text-[12.5px] text-text2 mt-0.5">{b.text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-7 flex items-center gap-3 flex-wrap">
            {canEnable ? (
              <PrimaryButton onClick={() => setOpen(true)}>Enable Netly Pay</PrimaryButton>
            ) : (
              <p className="text-[12.5px] text-text3">
                Ask the shop owner to turn Netly Pay on for this shop.
              </p>
            )}
            <a
              href="https://netly.netlise.com"
              target="_blank"
              rel="noreferrer"
              className="text-[13px] font-medium text-brand hover:underline"
            >
              What members see
            </a>
          </div>
        </div>

        <div
          className="rounded-card border border-borderc min-h-[260px]"
          style={{
            background:
              'repeating-linear-gradient(135deg, var(--brandSoft) 0 10px, var(--surface2) 10px 20px)',
          }}
          aria-hidden="true"
        />
      </div>

      {open && (
        <Modal
          title={step === 4 ? 'Netly Pay is ready' : 'Enable Netly Pay'}
          sub={step === 4 ? 'Setup complete' : `Step ${step} of 3 · ${STEP_TITLES[step - 1]}`}
          width={780}
          progress={step === 4 ? 100 : Math.round(step * 33.3)}
          onClose={step === 4 ? () => finish() : reset}
          footer={
            step === 4 ? (
              <>
                <p className="text-[12.5px] text-text3">
                  Members can now join from your invite link.
                </p>
                <PrimaryButton onClick={() => finish()}>Go to overview</PrimaryButton>
              </>
            ) : (
              <>
                <p className="text-[12.5px] text-text3">
                  {touched && !valid
                    ? step === 1
                      ? 'Choose a business type to continue.'
                      : step === 2
                      ? 'Enter the Netly Pay account id.'
                      : 'Turn on at least one way to take payment.'
                    : error ?? 'You can change this later in Settings.'}
                </p>
                <span className="flex items-center gap-2.5">
                  {step > 1 && <GhostButton onClick={() => setStep(step - 1)}>Back</GhostButton>}
                  <PrimaryButton onClick={next} disabled={saving}>
                    {saving ? 'Working…' : step === 3 ? 'Turn on Netly Pay' : 'Continue'}
                  </PrimaryButton>
                </span>
              </>
            )
          }
        >
          {step === 1 && (
            <div className="grid gap-3 sm:grid-cols-2">
              {BUSINESS_TYPES.map((b) => {
                const on = template === b.id;
                return (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => setTemplate(b.id)}
                    className={`text-left p-4 rounded-xl border transition-colors ${
                      on ? 'border-brand bg-brand-soft/40' : 'border-borderc hover:bg-surface2'
                    }`}
                  >
                    <span className="flex items-start gap-3">
                      <Radio on={on} />
                      <span>
                        <span className="block text-[13.5px] font-semibold text-text1">
                          {b.label}
                        </span>
                        <span className="block text-[12.5px] text-text2 mt-0.5">{b.desc}</span>
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {step === 2 && (
            <div className="flex flex-col gap-3">
              {[
                {
                  id: 'create' as const,
                  label: 'Create a new Netly Pay account',
                  desc: 'Uses your Netlise shop name and owner phone.',
                },
                {
                  id: 'link' as const,
                  label: 'Link an existing Netly Pay account',
                  desc: 'Keeps your members, requests and history.',
                },
              ].map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => setMode(o.id)}
                  className={`text-left p-4 rounded-xl border transition-colors ${
                    mode === o.id ? 'border-brand bg-brand-soft/40' : 'border-borderc hover:bg-surface2'
                  }`}
                >
                  <span className="flex items-start gap-3">
                    <Radio on={mode === o.id} />
                    <span>
                      <span className="block text-[13.5px] font-semibold text-text1">{o.label}</span>
                      <span className="block text-[12.5px] text-text2 mt-0.5">{o.desc}</span>
                    </span>
                  </span>
                </button>
              ))}

              {mode === 'link' && (
                <div className="mt-1">
                  <label
                    htmlFor="np-workspace"
                    className="block text-[12.5px] font-medium text-text2 mb-1.5"
                  >
                    Netly Pay account id or phone
                  </label>
                  <input
                    id="np-workspace"
                    value={workspaceId}
                    onChange={(e) => setWorkspaceId(e.target.value)}
                    placeholder="ws_… or 077 123 4501"
                    className={`${inputCls} font-mono ${
                      touched && !workspaceId.trim() ? 'border-neg' : ''
                    }`}
                  />
                  <p className="text-[11.5px] text-text3 mt-1.5">
                    The account's owner email must match yours. Netlise support can link accounts
                    that do not.
                  </p>
                </div>
              )}

              {mode === 'create' && (
                <div className="mt-1 rounded-xl border border-borderc bg-surface2 p-4">
                  <p className="text-[12.5px] text-text2">
                    A new account will be created for{' '}
                    <strong className="text-text1">{shop?.shopName ?? 'this shop'}</strong>. You stay
                    the owner, and nothing is charged.
                  </p>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="flex flex-col gap-3">
              <div
                className={`rounded-xl border p-4 ${
                  touched && !valid ? 'border-neg' : 'border-borderc'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-[13.5px] font-semibold text-text1">Bank transfer</p>
                    <p className="text-[12.5px] text-text2 mt-0.5">
                      Members see these details and send a slip.
                    </p>
                  </div>
                  <Switch on={bankOn} onClick={() => setBankOn(!bankOn)} />
                </div>

                {bankOn && (
                  <div className="grid gap-3 sm:grid-cols-2 mt-4">
                    {([
                      ['Bank', 'bank', false],
                      ['Branch', 'branch', false],
                      ['Account name', 'name', false],
                      ['Account number', 'number', true],
                    ] as [string, keyof typeof bank, boolean][]).map(([label, key, mono]) => (
                      <div key={key}>
                        <label
                          htmlFor={`np-bank-${key}`}
                          className="block text-[12px] font-medium text-text2 mb-1.5"
                        >
                          {label}
                        </label>
                        <input
                          id={`np-bank-${key}`}
                          value={bank[key]}
                          onChange={(e) => setBank({...bank, [key]: e.target.value})}
                          className={`${inputCls} ${mono ? 'font-mono tabular-nums' : ''}`}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-xl border border-borderc p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-[13.5px] font-semibold text-text1">LankaQR</p>
                    <p className="text-[12.5px] text-text2 mt-0.5">
                      Upload the QR image from your bank in Settings once this is on.
                    </p>
                  </div>
                  <Switch on={qrOn} onClick={() => setQrOn(!qrOn)} />
                </div>
              </div>

              <div className="rounded-xl border border-borderc p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-[13.5px] font-semibold text-text1">Cash</p>
                    <p className="text-[12.5px] text-text2 mt-0.5">
                      Taken at the desk or at the POS counter.
                    </p>
                  </div>
                  <Switch on={cashOn} onClick={() => setCashOn(!cashOn)} />
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="flex flex-col gap-4">
              <p className="text-[13.5px] text-text2">
                {shop?.shopName ?? 'This shop'} can now send payment requests and take payments by{' '}
                {[bankOn && 'bank transfer', qrOn && 'LankaQR', cashOn && 'cash']
                  .filter(Boolean)
                  .join(', ')}
                .
              </p>
              <div className="grid gap-3 sm:grid-cols-3">
                {[
                  {n: '01', title: 'Import members', text: 'Bring in your list from a spreadsheet.', to: '/members'},
                  {n: '02', title: 'Create a program', text: 'Set up batches, fees and the timetable.', to: '/members'},
                  {n: '03', title: 'Invite staff', text: 'Give coaches and the front desk access.', to: '/employees'},
                ].map((s) => (
                  <button
                    key={s.n}
                    type="button"
                    onClick={() => finish(s.to)}
                    className="text-left p-4 rounded-xl border border-borderc hover:border-brand hover:bg-surface2 transition-colors"
                  >
                    <span className="block text-[11px] font-mono text-text3">{s.n}</span>
                    <span className="block text-[13.5px] font-semibold text-text1 mt-1.5">
                      {s.title}
                    </span>
                    <span className="block text-[12px] text-text2 mt-0.5">{s.text}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
};

export default EnableNetlyPayView;
